import { useState, useEffect } from "react";
import { useFormik } from "formik";
import {
	Button,
	Col,
	Form,
	FormFeedback,
	FormGroup,
	Input,
	Label,
	Row,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
} from "reactstrap";
import Swal from "sweetalert2";
import Select from "react-select";
import IndicadoresConfigurationService from "services/IndicadoresConfigurationService";
import { graphicType, typeConfiguration } from ".";
import { customStyles } from "../..";
import { Checkbox } from "@mui/material";
import { CheckSquare, FloppyDisk, Square, X } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";

const totalizadorInfos = [
	{ value: "", label: "Nenhum" },
	{ value: "sum", label: "Soma" },
	{ value: "average", label: "Média" },
	{ value: "calc", label: "Cálculo" },
];

const quantifierInfos = [
	{ value: "", label: "Nenhum" },
	{ value: "R$", label: "R$" },
	{ value: "%", label: "%" },
	{ value: "x", label: "x" },
	{ value: "h", label: "h" },
	{ value: "Un", label: "Un" },
	{ value: "pts", label: "pts" },
];

const useGoalConfigForm = (
	goalConfig,
	nextSeq,
	setShowModal,
	goalId,
	getItems,
	allGoalConfig,
	setLoading,
) => {
	const formik = useFormik({
		initialValues: {
			id: goalConfig?.id,
			name: goalConfig?.name || "",
			type: goalConfig?.type || "",
			quantifier: goalConfig?.quantifier || "",
			totalizator: goalConfig?.totalizator || "",
			sequence: goalConfig?.sequence || nextSeq,
			display_on_chart: goalConfig?.display_on_chart,
			color: goalConfig?.color || "#000000",
			formula: goalConfig?.formula || "",
			only_read: goalConfig?.only_read || 0,
			show_on_second_axis: goalConfig?.show_on_second_axis || 0,
			totalizator_ref: goalConfig?.totalizator_ref || null,
			decimals_cases: goalConfig?.decimals_cases || 0,
			type_meta_definition: goalConfig?.type_meta_definition || "",
		},
		validate: (values) => {
			const errors = {};
			if (!values.name) errors.name = "Nome é obrigatório";
			if (!values.type) errors.type = "Tipo é obrigatório";
			if (values.type === "0" && !values.type_meta_definition)
				errors.type_meta_definition = "Tipo de meta é obrigatório";
			return errors;
		},
		onSubmit: async (values, { resetForm }) => {
			setLoading(true);
			values.totalizator_ref =
				values.totalizator_ref === "" ? null : values.totalizator_ref;
			
			// Se não for tipo Meta, remove o campo type_meta_definition
			if (values.type !== "0") {
				delete values.type_meta_definition;
			}

			if (!values.id) {
				if (checkSequence(values.sequence, allGoalConfig)) {
					setLoading(false);
					SendaCustomSwal.fire({
						iconHtml: '<i class="error-icon"></i>',
						title: "Erro",
						text: "Já existe uma configuração com esta sequência.",
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					});
					return;
				}
			}

			if (
				(values.type == "0" || values.type == "1") &&
				checkIfTypeExistsInConfig(values.type, allGoalConfig) &&
				!values.id
			) {
				setLoading(false);
				SendaCustomSwal.fire({
					iconHtml: '<i class="error-icon"></i>',
					title: "Erro",
					text: "Já existe uma configuração com este tipo para esta meta.",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
				return;
			}

			values.goals_id = goalId;
			
			// Tratar valores null para campos que não aceitam null no banco
			if (values.only_read === null) {
				values.only_read = 0;
			}
			if (values.show_on_second_axis === null) {
				values.show_on_second_axis = 0;
			}
			
			try {
				const response = values.id
					? await IndicadoresConfigurationService.put(
							values.id,
							values,
					  )
					: await IndicadoresConfigurationService.post(values);
				SendaCustomSwal.fire({
					iconHtml: '<i class="success-icon"></i>',
					title: "Sucesso",
					text: `Registro ${
						values.id ? "atualizado" : "inserido"
					} com sucesso!`,
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
				resetForm();
				getItems();
				setShowModal(false);
			} catch (error) {
				console.error(error);
				SendaCustomSwal.fire({
					iconHtml: '<i class="error-icon"></i>',
					title: "Erro",
					text: "Erro ao salvar o registro",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			} finally {
				setLoading(false);
			}
		},
	});

	useEffect(() => {
		if (goalConfig) {
			formik.setValues({
				...formik.values,
				...goalConfig,
				sequence: goalConfig.sequence || nextSeq,
				color: goalConfig.color || "#000000",
				type_meta_definition: goalConfig.type_meta_definition,
			});
		}
	}, [goalConfig, nextSeq]);

	return formik;
};

const SelectField = ({
	id,
	name,
	options,
	value,
	onChange,
	isDisabled,
	customStyles,
	placeholder,
}) => (
	<Select
		id={id}
		name={name}
		options={options}
		styles={customStyles}
		value={value}
		onChange={onChange}
		isDisabled={isDisabled}
		placeholder={placeholder}
		menuPortalTarget={document.body}
		menuPosition="fixed"
	/>
);

const checkIfTypeExistsInConfig = (type, allGoalConfig) => {
	for (let i = 0; i < allGoalConfig.length; i++) {
		if (allGoalConfig[i].type === type) {
			return true;
		}
	}
	return false;
};

const checkSequence = (sequence, allGoalConfig) => {
	for (let i = 0; i < allGoalConfig.length; i++) {
		if (allGoalConfig[i].sequence == sequence) {
			return true;
		}
	}
	return false;
};

const FormConfiguration = ({
	goalConfig,
	setGoalConfig,
	handleUpdateTable,
	goalId,
	selectedTypes,
	nextSeq,
	showModal,
	setShowModal,
	getItems,
	allGoalConfig,
}) => {
	const [disableTotalizador, setDisableTotalizador] = useState(false);
	const [typeSelected, setTypeSelected] = useState();
	const [totalizator, setTotalizator] = useState();
	const [quantifier, setQuantifier] = useState();
	const [totalizatorRef, setTotalizatorRef] = useState(null);
	const [loading, setLoading] = useState(false);

	const formik = useGoalConfigForm(
		goalConfig,
		nextSeq,
		setShowModal,
		goalId,
		getItems,
		allGoalConfig,
		setLoading,
	);

	const handleCloseModal = () => {
		formik.resetForm();
		setTypeSelected(null);
		setTotalizator(null);
		setTotalizatorRef(null);
		setQuantifier(null);
		setShowModal(false);
	};

	const referenceOptions = [
		{ value: "", label: "Nenhum" },
		...allGoalConfig
			.filter((config) => config.type === "1")
			.map((config) => ({ value: config.id, label: config.name })),
	];

	useEffect(() => {
		if (showModal && goalConfig) {
			formik.resetForm();
			formik.setValues({
				id: goalConfig.id,
				name: goalConfig.name || "",
				type: goalConfig.type || "",
				quantifier: goalConfig.quantifier || "",
				totalizator: goalConfig.totalizator || "",
				sequence: goalConfig.sequence || nextSeq,
				display_on_chart: goalConfig.display_on_chart,
				color: goalConfig.color || "#000000",
				formula: goalConfig.formula || "",
				only_read: goalConfig.only_read,
				show_on_second_axis: goalConfig.show_on_second_axis,
				totalizator_ref: goalConfig.totalizator_ref,
				decimals_cases: goalConfig.decimals_cases || 0,
				type_meta_definition: goalConfig.type_meta_definition || "",
			});

			setTypeSelected(
				typeConfiguration.find(
					(tc) => tc.value === goalConfig.type?.toString(),
				) || { value: "", label: "Nenhum" },
			);
			setTotalizator(
				totalizadorInfos.find(
					(tc) => tc.value === goalConfig.totalizator?.toString(),
				) || { value: "", label: "Nenhum" },
			);
			setQuantifier(
				quantifierInfos.find(
					(tc) => tc.value === goalConfig.quantifier?.toString(),
				) || { value: "", label: "Nenhum" },
			);
			setDisableTotalizador(goalConfig.type === "1");

			const selectedReference = allGoalConfig.find(
				(config) => config.id === goalConfig.totalizator_ref,
			);
			if (selectedReference) {
				setTotalizatorRef({
					value: selectedReference.id,
					label: selectedReference.name,
				});
			} else {
				setTotalizatorRef(null);
			}
		} else {
			formik.resetForm();
			setTypeSelected({ value: "", label: "Nenhum" });
			setTotalizator({ value: "", label: "Nenhum" });
			setQuantifier({ value: "", label: "Nenhum" });
			setTotalizatorRef({ value: "", label: "Nenhum" });
		}
	}, [goalConfig, showModal, nextSeq, allGoalConfig]);

	return (
		<Modal
			isOpen={showModal}
			toggle={handleCloseModal}
			centered
			style={{ minWidth: 1000 }}
		>
			<ModalHeader toggle={handleCloseModal}>
				{goalConfig ? "Editar Configuração" : "Adicionar Configuração"}
			</ModalHeader>
			<Form onSubmit={formik.handleSubmit}>
				<ModalBody>
					<Row>
						<Col md="6">
							<FormGroup>
								<Label for="name">
									Nome <span className="text-danger">*</span>
								</Label>
								<Input
									id="name"
									name="name"
									type="text"
									{...formik.getFieldProps("name")}
									invalid={
										formik.touched.name &&
										!!formik.errors.name
									}
								/>
								<FormFeedback>
									{formik.errors.name}
								</FormFeedback>
							</FormGroup>
						</Col>
						<Col md="6">
							<FormGroup>
								<Label for="type">
									Tipo <span className="text-danger">*</span>
								</Label>
								<SelectField
									id="type"
									name="type"
									options={typeConfiguration}
									value={typeSelected}
									onChange={(selectedOption) => {
										formik.setFieldValue(
											"type",
											selectedOption.value,
										);
										setTypeSelected(selectedOption);
										setDisableTotalizador(
											selectedOption.value === "1",
										);
									}}
									customStyles={customStyles}
								/>
								{formik.touched.type && formik.errors.type && (
									<div className="text-danger">
										{formik.errors.type}
									</div>
								)}
							</FormGroup>
						</Col>
						<Col md="3">
							<FormGroup>
								<Label for="quantifier">Quantificador</Label>
								<SelectField
									id="quantifier"
									name="quantifier"
									options={quantifierInfos}
									value={quantifier}
									onChange={(selectedOption) => {
										formik.setFieldValue(
											"quantifier",
											selectedOption.value,
										);
										setQuantifier(selectedOption);
									}}
									customStyles={customStyles}
								/>
							</FormGroup>
						</Col>
						<Col md="3">
							<FormGroup>
								<Label for="totalizator">Totalizador</Label>
								<SelectField
									id="totalizator"
									name="totalizator"
									options={totalizadorInfos}
									value={totalizator}
									onChange={(selectedOption) => {
										formik.setFieldValue(
											"totalizator",
											selectedOption.value,
										);
										setTotalizator(selectedOption);
									}}
									// isDisabled={disableTotalizador} // pediram pra desativar isso
									customStyles={customStyles}
								/>
							</FormGroup>
						</Col>
						<Col md="3">
							<FormGroup>
								<Label for="display_on_chart">
									Exibir no gráfico
								</Label>
								<SelectField
									placeholder="Selecione"
									id="display_on_chart"
									name="display_on_chart"
									options={graphicType}
									value={
										graphicType[
											formik.values.display_on_chart
										]
									}
									onChange={(selectedOption) => {
										formik.setFieldValue(
											"display_on_chart",
											selectedOption.value,
										);
									}}
									customStyles={customStyles}
								/>
							</FormGroup>
						</Col>
						<Col md="3">
							<FormGroup>
								<Label for="decimals_cases">
									Casas decimais
								</Label>
								<Input
									id="decimals_cases"
									name="decimals_cases"
									type="number"
									{...formik.getFieldProps("decimals_cases")}
									invalid={
										formik.touched.decimals_cases &&
										!!formik.errors.decimals_cases
									}
								/>
								<FormFeedback>
									{formik.errors.decimals_cases}
								</FormFeedback>
							</FormGroup>
						</Col>
						<Col md="3">
							<FormGroup>
								<Label for="color">Cor</Label>
								<Input
									id="color"
									name="color"
									type="color"
									value={formik.values.color || "#000000"} // Define um valor padrão se o valor for nulo
									onChange={(event) =>
										formik.setFieldValue(
											"color",
											event.target.value,
										)
									}
									invalid={
										formik.touched.color &&
										!!formik.errors.color
									}
								/>
								<FormFeedback>
									{formik.errors.color}
								</FormFeedback>
							</FormGroup>
						</Col>
						<Col md="3">
							<FormGroup>
								<Label for="formula">Fórmula</Label>
								<Input
									id="formula"
									name="formula"
									type="text"
									{...formik.getFieldProps("formula")}
									invalid={
										formik.touched.formula &&
										!!formik.errors.formula
									}
								/>
								<FormFeedback>
									{formik.errors.formula}
								</FormFeedback>
							</FormGroup>
						</Col>
						<Col md="3">
							<FormGroup>
								<Label for="reference_column">
									Coluna Referência
								</Label>
								<SelectField
									id="reference_column"
									name="reference_column"
									options={referenceOptions}
									value={totalizatorRef}
									isDisabled={disableTotalizador}
									onChange={(selectedOption) => {
										formik.setFieldValue(
											"totalizator_ref",
											selectedOption.value,
										);
										setTotalizatorRef(selectedOption);
									}}
									customStyles={customStyles}
								/>
							</FormGroup>
						</Col>
						<Col md="3">
							<FormGroup>
								<Label for="sequence">Sequência</Label>
								<Input
									id="sequence"
									name="sequence"
									type="number"
									{...formik.getFieldProps("sequence")}
									invalid={
										formik.touched.sequence &&
										!!formik.errors.sequence
									}
								/>
								<FormFeedback>
									{formik.errors.sequence}
								</FormFeedback>
							</FormGroup>
						</Col>
						<Col md="4">
							<FormGroup check>
								<div style={{ display: "flex", alignItems: "center" }}>
									<Checkbox
										id="only_read"
										name="only_read"
										checked={formik.values.only_read === 1}
										onChange={(event) => {
											const value = event.target.checked ? 1 : 0;
											formik.setFieldValue("only_read", value);
										}}
										onClick={(event) => {
											// Previne o comportamento padrão para evitar duplo toggle
											event.preventDefault();
											const newValue = formik.values.only_read === 1 ? 0 : 1;
											formik.setFieldValue("only_read", newValue);
										}}
										style={{
											background: "none",
											border: "none",
											padding: 0,
											cursor: "pointer",
											color: "black",
											marginRight: 10,
										}}
										icon={<Square size={16} />}
										checkedIcon={<CheckSquare size={16} />}
									/>
									<Label 
										htmlFor="only_read" 
										style={{ marginBottom: 0, cursor: "pointer" }}
										onClick={() => {
											const newValue = formik.values.only_read === 1 ? 0 : 1;
											formik.setFieldValue("only_read", newValue);
										}}
									>
										Habilitar fórmula
									</Label>
								</div>
							</FormGroup>
						</Col>
						<Col md="4">
							<FormGroup check>
								<div style={{ display: "flex", alignItems: "center" }}>
									<Checkbox
										id="show_on_second_axis"
										name="show_on_second_axis"
										checked={formik.values.show_on_second_axis == 1}
										onChange={(event) => {
											const value = event.target.checked ? 1 : 0;
											formik.setFieldValue("show_on_second_axis", value);
										}}
										onClick={(event) => {
											// Previne o comportamento padrão para evitar duplo toggle
											event.preventDefault();
											const newValue = formik.values.show_on_second_axis == 1 ? 0 : 1;
											formik.setFieldValue("show_on_second_axis", newValue);
										}}
										style={{
											background: "none",
											border: "none",
											padding: 0,
											cursor: "pointer",
											color: "black",
											marginRight: 10,
										}}
										icon={<Square size={16} />}
										checkedIcon={<CheckSquare size={16} />}
									/>
									<Label 
										htmlFor="show_on_second_axis" 
										style={{ marginBottom: 0, cursor: "pointer" }}
										onClick={() => {
											const newValue = formik.values.show_on_second_axis == 1 ? 0 : 1;
											formik.setFieldValue("show_on_second_axis", newValue);
										}}
									>
										Eixo secundário
									</Label>
								</div>
							</FormGroup>
						</Col>
						{formik.values.type === "0" && (
							<Col md="4">
								<FormGroup tag="fieldset">
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "10px",
										}}
									>
										<Label
											style={{
												marginBottom: 0,
												color:
													formik.values
														.type_meta_definition ===
													"maior_melhor"
														? "#495057"
														: "#999",
											}}
										>
											Maior Melhor
										</Label>
										<div
											style={{
												display: "flex",
												gap: "5px",
											}}
										>
											<Input
												type="radio"
												name="type_meta_definition"
												value="maior_melhor"
												defaultChecked={
													formik.values
														.type_meta_definition ===
													"maior_melhor"
												}
												onChange={() =>
													formik.setFieldValue(
														"type_meta_definition",
														"maior_melhor",
													)
												}
												className="black-radio"
												style={{
													cursor: "pointer",
													"--radio-color": "black",
												}}
											/>
											<Input
												type="radio"
												name="type_meta_definition"
												value="menor_melhor"
												defaultChecked={
													formik.values
														.type_meta_definition ===
													"menor_melhor"
												}
												onChange={() =>
													formik.setFieldValue(
														"type_meta_definition",
														"menor_melhor",
													)
												}
												className="black-radio"
												style={{
													cursor: "pointer",
													"--radio-color": "black",
												}}
											/>
										</div>
										<Label
											style={{
												marginBottom: 0,
												color:
													formik.values
														.type_meta_definition ===
													"menor_melhor"
														? "#495057"
														: "#999",
											}}
										>
											Menor Melhor
										</Label>
									</div>
									<style>
										{`
											.black-radio:checked {
												background-color: black !important;
												border-color: black !important;
											}
											.black-radio:focus {
												border-color: black !important;
												box-shadow: 0 0 0 0.25rem rgba(0, 0, 0, 0.25) !important;
											}
										`}
									</style>
									{formik.touched.type_meta_definition &&
										formik.errors.type_meta_definition && (
											<div className="text-danger">
												{
													formik.errors
														.type_meta_definition
												}
											</div>
										)}
								</FormGroup>
							</Col>
						)}
					</Row>
				</ModalBody>
				<ModalFooter>
					<Button
						style={{
							background: "none",
							padding: 10,
							cursor: "pointer",
							color: "black",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 2,
						}}
						type="submit"
						disabled={loading}
					>
						{loading ? (
							<span
								className="spinner-border spinner-border-sm mr-1"
								style={{ marginRight: 10 }}
							></span>
						) : null}
						<FloppyDisk size={16} /> Salvar
					</Button>
					<Button
						style={{
							background: "none",
							padding: 10,
							cursor: "pointer",
							color: "black",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 2,
						}}
						onClick={handleCloseModal}
					>
						<X size={16} /> Cancelar
					</Button>
				</ModalFooter>
			</Form>
		</Modal>
	);
};

export default FormConfiguration;
